import { useMemo, useCallback, useEffect } from 'react';
import { useGetGuestRidesQuery } from '../queries/getGuestRides.query.generated';
import { useRooftop, useUser } from '@store';
import {
  useGuestRideStore,
  type GuestRideStatusFilter,
  type GuestRideSortBy,
  type SortOrder,
  type GuestRideViewMode,
} from '../stores/guestRideStore';
import { useGuestRideQueryVariables } from '../hooks/useGuestRideQueryVariables';
import type { CountWindow } from '@features/products/counts/productCounts';
import { getEffectiveDateRange } from '../utils/dateRangeUtils';
import { applyOptimisticStatuses, isOptimisticStatusStale } from '../utils/guestRideUtils';


// If a call silently fails server-side the status never moves off the
// baseline, so the overlay expires after this window and the real status
// brings the Call/Retry button back.
const OPTIMISTIC_STATUS_TTL_MS = 20_000;

/**
 * The tab's figures come from the shared Guest Rides counts, so no stats query
 * runs here. The hook returns the date filter's window for that module.
 */
export function useRidesTabData() {
  const store = useGuestRideStore();
  const {
    filters,
    page,
    pageSize,
    sortBy,
    sortOrder,
    viewMode,
    highlightedRideId,
    optimisticStatuses,
    updateFilter,
    resetFilters,
    setPage,
    setPageSize,
    setSorting: storeSorting,
    setViewMode,
    setHighlightedRideId,
    clearOptimisticStatus,
  } = store;

  const { statusFilter, search, dateRange, startDate, endDate } = filters;

  const rooftop = useRooftop();
  const user = useUser();
  const customerId = rooftop?.id || user?.profile?.customer_id || 0;

  const queryVariables = useGuestRideQueryVariables(customerId);

  const { data, loading, error, refetch } = useGetGuestRidesQuery({
    variables: queryVariables,
    fetchPolicy: 'cache-and-network',
    skip: !customerId,
  });

  // A ride keeps its optimistic `pending` status only while the server still
  // reports the baseline it had at call time. Once the server moves it, the
  // server wins.
  const rides = useMemo(() => {
    if (!data?.guestrides) return [];
    return applyOptimisticStatuses(data.guestrides, optimisticStatuses);
  }, [data, optimisticStatuses]);

  // Expiring entries are cleared on a timer so the button reappears even if
  // nothing else re-renders in the meantime.
  useEffect(() => {
    const ids = Object.keys(optimisticStatuses).map(Number);
    if (ids.length === 0) return;

    const ridesById = new Map((data?.guestrides ?? []).map(r => [r.id, r]));
    const timers: ReturnType<typeof setTimeout>[] = [];

    ids.forEach((id) => {
      const optimistic = optimisticStatuses[id];
      const ride = ridesById.get(id);
      if (isOptimisticStatusStale(optimistic, ride, Date.now(), OPTIMISTIC_STATUS_TTL_MS)) {
        clearOptimisticStatus(id);
        return;
      }
      const remaining = OPTIMISTIC_STATUS_TTL_MS - (Date.now() - optimistic.at);
      timers.push(setTimeout(() => clearOptimisticStatus(id), remaining));
    });

    return () => timers.forEach(clearTimeout);
  }, [data, optimisticStatuses, clearOptimisticStatus]);

  const filteredCount = data?.guestrides_aggregate?.aggregate?.count ?? 0;

  // The date filter's window, as the shared counts take it; a custom range without both ends is all time.
  const window = useMemo<CountWindow>(() => {
    const effectiveRange = getEffectiveDateRange(dateRange, startDate, endDate);
    return effectiveRange ? { startIso: effectiveRange.start, endIso: effectiveRange.end } : null;
  }, [dateRange, startDate, endDate]);

  const setStatusFilter = useCallback(
    (filter: GuestRideStatusFilter) => updateFilter('statusFilter', filter),
    [updateFilter],
  );

  const setSearch = useCallback(
    (value: string) => updateFilter('search', value),
    [updateFilter],
  );

  const setRowsPerPage = useCallback(
    (value: number) => setPageSize(value),
    [setPageSize],
  );

  const handleSetSorting = useCallback(
    (newSortBy: GuestRideSortBy, newSortOrder: SortOrder) => storeSorting(newSortBy, newSortOrder),
    [storeSorting],
  );

  return {
    rides,
    filteredCount,
    customerId,
    window,
    statusFilter,
    search,
    page,
    rowsPerPage: pageSize,
    viewMode,
    sortBy,
    sortOrder,
    highlightedRideId,
    loading,
    error,
    refetch,
    setStatusFilter,
    setSearch,
    setPage,
    setRowsPerPage,
    setViewMode,
    setSorting: handleSetSorting,
    clearFilters: resetFilters,
    setHighlightedRideId,
  };
}

export type { GuestRideStatusFilter, GuestRideSortBy, SortOrder, GuestRideViewMode };
